import { useEffect, useState } from 'react';
import { api } from '../lib/api.ts';
import { Icon } from './Icon.tsx';
import './lesson-library.css';

type CatalogLesson = {
  id: string;
  version: string;
  title: string;
  objective: string;
  grade: number;
  theme?: string;
};

export function LessonLibrary() {
  const [lessons, setLessons] = useState<CatalogLesson[]>([]);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    api<{ lessons: CatalogLesson[] }>('/api/lessons')
      .then((r) => {
        if (active) setLessons(r.lessons);
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, []);
  return (
    <main className="lesson-library">
      <header>
        <a href="/" className="brand">
          <span className="brand-mark">
            <Icon name="spark" />
          </span>
          enactry
        </a>
        <span>Learn it. See it. Make it yours.</span>
      </header>
      <section className="library-intro">
        <span className="eyebrow">Your learning workshops</span>
        <h1>
          One idea can open
          <br />a whole new world.
        </h1>
        <p>
          Start with a tutor-led example. Build a model, try your reasoning, and
          see what you can do in a new situation. Each workshop keeps its own
          progress and notebook.
        </p>
      </section>
      {error && (
        <p role="alert">
          {error}{' '}
          <button onClick={() => window.location.reload()}>Try again</button>
        </p>
      )}
      {!error && !lessons.length && (
        <p role="status">Opening the workshop shelf…</p>
      )}
      <div className="library-grid">
        {lessons.map((item) => (
          <article key={`${item.id}@${item.version}`} className="library-card">
            <WorkshopArtwork theme={item.theme} />
            <div className="library-card-copy">
              <span className="eyebrow">
                Grade {item.grade} · Algebra · Concept + application
              </span>
              <h2>{item.title}</h2>
              <p>{item.objective}</p>
              <div className="library-meta">
                <span>3 learning activities</span>
                <span>Voice, tiles & writing</span>
              </div>
              <a
                className="primary"
                href={`/?lesson=${encodeURIComponent(item.id)}&version=${encodeURIComponent(item.version)}`}
              >
                Open workshop <Icon name="arrow" />
              </a>
            </div>
          </article>
        ))}
      </div>
      <p className="library-footnote">
        Your tutor can explain, replay, or give you thinking time. There is no
        timer.
      </p>
    </main>
  );
}

export function WorkshopArtwork({ theme }: { theme?: string }) {
  return (
    <svg
      className={`workshop-artwork theme-${theme ?? 'rental'}`}
      viewBox="0 0 600 230"
      aria-hidden="true"
    >
      <rect
        width="600"
        height="230"
        fill={/garden/i.test(theme ?? '') ? '#e5edd6' : '#e9efdf'}
      />
      <circle cx="488" cy="51" r="24" fill="#ecd693" />
      <path d="M0 179Q170 115 310 184T600 154V230H0Z" fill="#c1d1a8" />
      {/garden/i.test(theme ?? '') ? (
        <g stroke="#54795b" strokeWidth="5" strokeLinecap="round">
          <path d="M65 154h470l-26 55H91Z" fill="#b79568" stroke="#9a784e" />
          {[140, 245, 350, 455].map((x, i) => (
            <g key={x}>
              <path d={`M${x} 164V${90 + (i % 2) * 14}`} fill="none" />
              <path
                d={`M${x} 128q-48-4-42-37 44 0 42 37m0-9q44-4 39-34-38 0-39 34`}
                fill="#789e66"
              />
            </g>
          ))}
          <path d="M97 181h405" stroke="#8d6948" />
        </g>
      ) : (
        <g
          fill="none"
          stroke="#42695b"
          strokeWidth="7"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="186" cy="157" r="48" />
          <circle cx="398" cy="157" r="48" />
          <path d="m186 157 66-88 68 88H186l51-67h132l29 67M320 157l49-98h-22m-110 10h42" />
          <path d="m257 72 22 7" stroke="#c29b55" />
        </g>
      )}
      <g transform="translate(42 30)">
        <rect width="135" height="41" rx="12" fill="#fffef5" />
        <text
          x="67"
          y="27"
          textAnchor="middle"
          fill="#2c5947"
          fontFamily="Georgia,serif"
          fontSize="23"
        >
          x = ?
        </text>
      </g>
    </svg>
  );
}
